import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ErpGroupOption } from "../api/leads";
import { CountrySelect } from "./CountrySelect";
import { Select } from "./Select";

/**
 * What a customer needs for the ERP to open it, entered the same wherever the portal makes a customer: when a won
 * lead is converted and when a customer is entered by hand. The ERP opens a customer only with a tax number, a
 * registration number, a country, a customer group and an address. The group is picked from the ERP's tree by its
 * path there, and none is picked in advance: where the customer is filed is the seller's call. For Serbia the
 * numbers have a fixed length, checked here and again on the server.
 */
export type ErpCustomer = {
  vatNumber: string;
  registrationNumber: string;
  country: string;
  addressLine1: string;
  postalCode: string;
  city: string;
  erpGroup: string;
};

/** Where the numbers break the Serbian rule, and whether the ERP would open the customer. */
export function checkErpCustomer(v: ErpCustomer) {
  const serbia = v.country === "RS";
  const vat = v.vatNumber.trim();
  const registration = v.registrationNumber.trim();
  const vatWrong = serbia && vat !== "" && !/^\d{9}$/.test(vat);
  const registrationWrong = serbia && registration !== "" && !/^\d{8}$/.test(registration);
  const ready = vat !== "" && registration !== "" && v.country !== "" && v.erpGroup !== ""
    && v.addressLine1.trim() !== "" && v.postalCode.trim() !== "" && v.city.trim() !== ""
    && !vatWrong && !registrationWrong;
  return { vatWrong, registrationWrong, ready };
}

/** The values as they go to the server, without the blanks around them. */
export function trimErpCustomer(v: ErpCustomer): ErpCustomer {
  return {
    vatNumber: v.vatNumber.trim(),
    registrationNumber: v.registrationNumber.trim(),
    country: v.country,
    erpGroup: v.erpGroup,
    addressLine1: v.addressLine1.trim(),
    postalCode: v.postalCode.trim(),
    city: v.city.trim(),
  };
}

export function ErpCustomerFields({
  value,
  onChange,
  groups,
  autoFocus,
}: {
  value: ErpCustomer;
  onChange: (next: ErpCustomer) => void;
  /** The ERP's customer groups in the order of its tree, each labelled with its path there. */
  groups: ErpGroupOption[];
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const { vatWrong, registrationWrong } = checkErpCustomer(value);
  const set = (patch: Partial<ErpCustomer>) => onChange({ ...value, ...patch });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={row}>
        <Field label={t("erpCustomer.vatNumber", "Tax number (PIB)")} hint={vatWrong ? t("erpCustomer.vatHint", "A Serbian tax number has 9 digits.") : undefined} style={col}>
          <input className="input" value={value.vatNumber} onChange={(e) => set({ vatNumber: e.target.value })} inputMode="numeric" maxLength={20} autoFocus={autoFocus} />
        </Field>
        <Field label={t("erpCustomer.registrationNumber", "Registration number")} hint={registrationWrong ? t("erpCustomer.registrationHint", "A Serbian registration number has 8 digits.") : undefined} style={col}>
          <input className="input" value={value.registrationNumber} onChange={(e) => set({ registrationNumber: e.target.value })} inputMode="numeric" maxLength={20} />
        </Field>
      </div>
      <div style={row}>
        <Field label={t("erpCustomer.country", "Country")} style={col}>
          <CountrySelect value={value.country} onChange={(country) => set({ country })} ariaLabel={t("erpCustomer.country", "Country")} />
        </Field>
        <Field label={t("erpCustomer.erpGroup", "Customer kind (ERP group)")} style={col}>
          <Select<string>
            value={value.erpGroup}
            onChange={(erpGroup) => set({ erpGroup })}
            ariaLabel={t("erpCustomer.erpGroup", "Customer kind (ERP group)")}
            placeholder={t("erpCustomer.erpGroupPick", "Pick a group")}
            options={groups}
          />
        </Field>
      </div>
      <Field label={t("erpCustomer.address", "Address")}>
        <input className="input" value={value.addressLine1} onChange={(e) => set({ addressLine1: e.target.value })} maxLength={100} />
      </Field>
      <div style={row}>
        <Field label={t("erpCustomer.postalCode", "Postal code")} style={{ width: 140 }}>
          <input className="input" value={value.postalCode} onChange={(e) => set({ postalCode: e.target.value })} maxLength={20} />
        </Field>
        <Field label={t("erpCustomer.city", "City")} style={col}>
          <input className="input" value={value.city} onChange={(e) => set({ city: e.target.value })} maxLength={100} />
        </Field>
      </div>
    </div>
  );
}

function Field({
  label, hint, children, style,
}: { label: string; hint?: string; children: ReactNode; style?: CSSProperties }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, ...style }}>
      <span style={fieldLabel}>{label}</span>
      {children}
      {hint && <span style={{ color: "var(--err)", fontSize: 12 }}>{hint}</span>}
    </label>
  );
}

const fieldLabel: CSSProperties = { fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 };
const row: CSSProperties = { display: "flex", gap: 12, flexWrap: "wrap" };
const col: CSSProperties = { flex: 1, minWidth: 160 };
